import React from 'react';
import CensorBar from '../oracle/CensorBar';
import AurumButton from '../oracle/AurumButton';
import GhostButton from '../oracle/GhostButton';
import TraitPicker from './TraitPicker';
import ScorePicker from './ScorePicker';
import { COPY } from '../../contracts/copyMap';
import {
  JUDGE_TRAITS,
  IMPROVEMENT_TRAITS,
  MIN_PICKS,
  MAX_PICKS,
  canSubmitSelection,
} from '../../contracts/scanFeedbackApi';

// One portrait, two columns, one submit.
//
// The image is ALWAYS the anonymized variant the server signed — there is no
// code path here that could render a processed portrait, because the card
// payload does not contain one. Nothing on this card identifies the owner: no
// handle, no score, no capture date.
//
// The ANONYMOUS censor bar sits over the portrait as a second, visual
// assertion of the same contract. The bar is already burned into the stored
// JPEG server-side; this is belt-and-braces, and it is what the brand does.
export default function ScanJudgeCard({
  card,
  strengths,
  improvements,
  onToggleStrength,
  onToggleImprovement,
  judgeScore,
  onScoreChange,
  onSubmit,
  onSkip,
  submitting,
  error,
}) {
  const canSubmit = canSubmitSelection(strengths, improvements, judgeScore);

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:gap-10" data-testid="scan-judge-card">
      {/* portrait */}
      <div className="relative w-full lg:w-[380px] shrink-0">
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-vein">
          <img
            src={card.anonymized_image_url}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            draggable={false}
            data-testid="judge-portrait"
          />
          <div className="absolute inset-x-0 top-1/3 flex justify-center pointer-events-none">
            <CensorBar mode="ANONYMOUS" size="lg" />
          </div>
        </div>
        <p className="font-display italic text-bone-dim text-xs mt-3">
          {COPY.SCAN_JUDGE_ANONYMOUS_NOTE}
        </p>
      </div>

      {/* selections */}
      <div className="flex flex-col gap-7 min-w-0 flex-1">
        {/* FIRST, as of this change. It used to sit last, on the argument that
            the chips are the judge's reasoning and the number is the summary of
            it — asking for the number first anchors the chips to the score
            rather than the other way round.

            That ordering also buried the single required field below two
            multi-select grids on a phone, so the submit button read as broken
            for anyone who filled the chips and never scrolled back. The
            anchoring cost is real; the invisible-required-field cost was
            costing judgments. If the calibration panel shows chip selections
            drifting toward the rating, this is the change to look at first. */}
        <ScorePicker value={judgeScore} onChange={onScoreChange} />

        <TraitPicker
          testId="judge-strengths"
          title={COPY.SCAN_JUDGE_STRENGTHS_PROMPT}
          traits={JUDGE_TRAITS}
          selected={strengths}
          disabledKeys={improvements}
          onToggle={onToggleStrength}
          max={MAX_PICKS}
        />

        <TraitPicker
          testId="judge-improvements"
          title={COPY.SCAN_JUDGE_IMPROVEMENTS_PROMPT}
          traits={IMPROVEMENT_TRAITS}
          selected={improvements}
          disabledKeys={strengths}
          onToggle={onToggleImprovement}
          max={MAX_PICKS}
          hint={COPY.SCAN_JUDGE_PICK_HINT}
        />

        <div className="flex flex-col gap-3">
          <AurumButton
            onClick={onSubmit}
            disabled={!canSubmit}
            loading={submitting}
            fullWidth
            data-testid="judge-submit"
          >
            {COPY.SCAN_JUDGE_SUBMIT_CTA}
          </AurumButton>

          {/* The disabled button explains itself rather than leaving the user
              to work out why nothing happens. */}
          {!canSubmit && (
            <p className="font-display italic text-bone-dim text-xs text-center">
              {COPY.SCAN_JUDGE_SUBMIT_DISABLED_HINT}
            </p>
          )}

          <GhostButton onClick={onSkip} disabled={submitting}>
            {COPY.SCAN_JUDGE_SKIP_CTA}
          </GhostButton>

          {/* Bone, never red — DESIGN.md forbids red even for errors. */}
          {error && (
            <p className="font-display text-bone text-sm text-center" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export { MIN_PICKS, MAX_PICKS };
